import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  ContextMenuOptionColors,
  ContextMenuOptionType
} from '@inno-ui/context-menu'
import {
  ArrowCirclePathRightIcon,
  ChainLinkIcon,
  StarsLineIcon,
  TrashLineIcon
} from '@inno-ui/icons'
import { Modal, ModalVariants } from '@inno-ui/modal'
import { useQueryClient } from '@tanstack/react-query'
import { hasAppAccess } from 'modules/app-registry/hooks/hasAppAccess'
import { useTranslation } from 'react-i18next'
import { useGetAppsQuery } from 'shared/api/containerApi/requests'
import {
  GET_ARTIFACTS_QUERY_KEY,
  getForgeToken,
  isForgeConfigured,
  useDeleteArtifactMutation,
  useGetArtifactsQuery
} from 'shared/api/forgeApi'
import { WidgetSize } from 'shared/constants/widgets'
import { Z_OVERLAY, Z_OVERLAY_MODAL } from 'shared/constants/zIndex'
import { PersistedWidgetSpec } from 'shared/types/widgets'

import { WidgetComponent } from '../../../../types/widgets.types'
import { ViewportMount } from '../../../ViewportMount/ViewportMount'
import { WidgetWrapper } from '../../../WidgetWrapper'
import { AiArtifactConfig, ArtifactSummary } from '../../types'
import { openRefineInChat } from '../../utils/openRefineInChat'
import { ArtifactFrame } from '../ArtifactFrame'
import { ArtifactUnavailable } from '../ArtifactUnavailable'
import { ShareArtifactPanel } from '../ShareArtifactPanel'
import styles from './AiArtifactTileWidget.module.scss'

export const AiArtifactTileWidget: WidgetComponent = ({
  widget,
  ...widgetProps
}) => {
  const persisted = widget as PersistedWidgetSpec
  const config = persisted.config as AiArtifactConfig | undefined
  const publicId = config?.publicId

  // separate overlay, not the sidebar's in-place full-view
  const [maximized, setMaximized] = useState(false)
  const [isSharing, setIsSharing] = useState(false)
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  // mini tile has no bar, reload lives in the right-click menu
  const reloadRef = useRef<() => void>()
  // separate ref so modal refresh targets the full-view iframe, not the mini tile
  const fullViewReloadRef = useRef<() => void>()
  const { t } = useTranslation('translation', { keyPrefix: 'widgets.aiAgent' })

  const queryClient = useQueryClient()
  const { data: containerAppsData = [] } = useGetAppsQuery()

  // tile only stores publicId, look up title and ownership from the gallery list
  const enabled = isForgeConfigured() && Boolean(getForgeToken())
  const { data, isSuccess, isError, refetch } = useGetArtifactsQuery()

  const artifact = useMemo<ArtifactSummary | null>(() => {
    if (!publicId) return null
    return (data ?? []).find(a => a.public_id === publicId) ?? null
  }, [data, publicId])

  const artifactWidgetProps = {
    ...widgetProps,
    widget,
    widgetName: artifact?.title || widgetProps.widgetName,
    fillMode: true
  }

  // only flag as revoked once we've seen this in a successful fetch
  const wasEverLoaded = useRef(false)
  useEffect(() => {
    if (artifact) wasEverLoaded.current = true
  }, [artifact])

  // all remove paths (context menu ×, accessRevoked effect) funnel through this;
  // once-guard prevents double-delete since WidgetWrapper and the effect can both fire.
  // widgetProps.onRemove is the grid's removal. It already drops the item
  // locally and calls the delete mutation.
  const removedRef = useRef(false)
  const removeTile = useCallback(() => {
    if (!persisted.id || removedRef.current) return
    removedRef.current = true
    widgetProps.onRemove?.()
  }, [persisted.id, widgetProps])

  const deleteMutation = useDeleteArtifactMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [GET_ARTIFACTS_QUERY_KEY] })
      setConfirmingDelete(false)
      // artifact deleted server-side, remove the tile too
      removeTile()
    }
  })

  // missing from a successful fetch means access was revoked
  const accessRevoked = Boolean(
    enabled &&
      isSuccess &&
      publicId &&
      data &&
      !artifact &&
      wasEverLoaded.current
  )

  useEffect(() => {
    if (accessRevoked) removeTile()
  }, [accessRevoked, removeTile])

  if (!publicId) {
    return (
      <WidgetWrapper {...artifactWidgetProps}>
        <div className={styles.empty}>{t('noWidgetForTile')}</div>
      </WidgetWrapper>
    )
  }

  // show empty state right away while the effect above unpins
  if (accessRevoked) {
    return (
      <WidgetWrapper {...artifactWidgetProps}>
        <div className={styles.empty}>{t('unavailable')}</div>
      </WidgetWrapper>
    )
  }

  // forge server down, skip the blank iframe
  if (enabled && isError) {
    return (
      <WidgetWrapper {...artifactWidgetProps}>
        <ArtifactUnavailable compact onRetry={() => refetch()} />
      </WidgetWrapper>
    )
  }

  const shareTarget: ArtifactSummary = artifact ?? {
    public_id: publicId,
    title: 'Widget',
    default_size: WidgetSize.MEDIUM,
    mine: true
  }

  const isShared = artifact ? !artifact.mine : false
  // owner, or an org admin/reviewer: forge sets can_edit for both
  const isOwner = artifact?.can_edit ?? artifact?.mine ?? false
  const canRefine = isOwner && hasAppAccess('agent', containerAppsData)

  const handleDelete = () => setConfirmingDelete(true)

  const frameActions = {
    sharedWithYou: isShared,
    sharedByName: artifact?.shared_by_name,
    onShare: isOwner ? () => setIsSharing(true) : undefined,
    onDelete: isOwner ? handleDelete : undefined,
    onRefine: canRefine ? () => openRefineInChat(publicId) : undefined,
    // anyone can unpin their own tile, doesn't affect other users
    onRemoveFromDesktop: removeTile
  }

  const tileMenuOptions: ContextMenuOptionType[] = [
    {
      icon: ArrowCirclePathRightIcon,
      label: t('refresh'),
      onClick: () => reloadRef.current?.()
    },
    ...(canRefine
      ? [
          {
            icon: StarsLineIcon,
            label: t('refine'),
            onClick: () => openRefineInChat(publicId)
          }
        ]
      : []),
    ...(isOwner
      ? [
          {
            icon: ChainLinkIcon,
            label: t('share'),
            onClick: () => setIsSharing(true)
          },
          { isSeparator: true },
          {
            icon: TrashLineIcon,
            label: t('deleteForEveryone'),
            color: ContextMenuOptionColors.Red,
            onClick: handleDelete
          }
        ]
      : [])
  ]

  return (
    <>
      {/* all remove paths go through removeTile to avoid duplicate deletes */}
      <WidgetWrapper
        {...artifactWidgetProps}
        onRemove={removeTile}
        contextMenuOptions={tileMenuOptions}
      >
        <ViewportMount
          active={!widgetProps.isCollapsed && !maximized}
          className={styles.frameSlot}
        >
          <ArtifactFrame
            publicId={publicId}
            view='mini'
            isInFullView={false}
            onToggleFullView={() => setMaximized(true)}
            hideBar
            reloadRef={reloadRef}
            {...frameActions}
          />
        </ViewportMount>
      </WidgetWrapper>

      <Modal
        isOpen={maximized}
        onClose={() => setMaximized(false)}
        closeOnClickOutside
        showCloseButton
        zIndex={Z_OVERLAY}
        maxWidth='90vw'
        maxHeight='90vh'
        contentFullWidth
        layers={[
          {
            title: artifact?.title,
            actionsLeft: canRefine
              ? [
                  {
                    icon: StarsLineIcon,
                    label: t('refine'),
                    onClick: () => openRefineInChat(publicId)
                  }
                ]
              : undefined,
            actionsRight: [
              {
                icon: ArrowCirclePathRightIcon,
                label: t('refresh'),
                onClick: () => fullViewReloadRef.current?.()
              },
              ...(isOwner
                ? [
                    {
                      icon: ChainLinkIcon,
                      label: t('share'),
                      onClick: () => {
                        setMaximized(false)
                        setIsSharing(true)
                      }
                    }
                  ]
                : [])
            ],
            bodyPaddings: false,
            body: (
              <ArtifactFrame
                publicId={publicId}
                view='full'
                isInFullView
                noOverlay
                hideBar
                onToggleFullView={() => setMaximized(false)}
                reloadRef={fullViewReloadRef}
                sharedWithYou={frameActions.sharedWithYou}
                sharedByName={frameActions.sharedByName}
              />
            )
          }
        ]}
      />

      <Modal
        isOpen={isSharing}
        onClose={() => setIsSharing(false)}
        closeOnClickOutside
        showCloseButton
        // sits above the maximized overlay (Z_OVERLAY = 9999) but below the
        // delete-confirm modal (Z_OVERLAY_MODAL = 10000) which can stack over it
        zIndex={Z_OVERLAY_MODAL}
        maxWidth={640}
        layers={[
          {
            title: t('shareAria', { title: shareTarget.title }),
            bodyPaddings: false,
            body: <ShareArtifactPanel artifact={shareTarget} />
          }
        ]}
      />

      {confirmingDelete && (
        <Modal
          isOpen
          variant={ModalVariants.DeletionConfirmation}
          // needs to sit above the maximize overlay
          zIndex={Z_OVERLAY_MODAL}
          title={t('deleteTitle')}
          description={t('deleteConfirm', { title: shareTarget.title })}
          isLoading={deleteMutation.isPending}
          onCancel={() => setConfirmingDelete(false)}
          onClose={() => setConfirmingDelete(false)}
          onDelete={() => deleteMutation.mutate(publicId)}
        />
      )}
    </>
  )
}
